import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { media } from '../services/api';
import api from '../services/api';

function MediaManager() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [existingMedia, setExistingMedia] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeCaption, setYoutubeCaption] = useState('');

  useEffect(() => {
    loadExistingMedia();
  }, []);

  const loadExistingMedia = async () => {
    try {
      const providerId = JSON.parse(localStorage.getItem('user')).id;
      const response = await media.getProviderMedia(providerId);
      setExistingMedia(response.data);
    } catch (error) {
      console.error('Failed to load existing media:', error);
    }
  };

  const handleFileUpload = async (type, files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setMessage('Uploading...');

    try {
      const formData = new FormData();

      if (type === 'gallery') {
        Array.from(files).forEach(file => {
          formData.append('media', file);
        });
      } else {
        formData.append(type, files[0]);
      }

      const response = await api.post(`/api/media/${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setMessage(response.data.message || `${type} uploaded successfully`);
      loadExistingMedia();
      setTimeout(() => setUploadProgress(0), 2000);
    } catch (error) {
      setMessage(error.response?.data?.error || `Failed to upload ${type}`);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setLoading(true);
      await media.deleteMedia(mediaId);
      setMessage('Item deleted successfully');
      await loadExistingMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      setMessage('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceImage = (type) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await handleFileUpload(type, [file]);
      }
    };
    fileInput.click();
  };

  const handleAddYouTubeVideo = async () => {
    if (!youtubeUrl) {
      setMessage('Please enter a YouTube URL');
      return;
    }

    setLoading(true);
    setMessage('Adding YouTube video...');

    try {
      await media.addYouTubeVideo(youtubeUrl, youtubeCaption);
      setMessage('YouTube video added successfully');
      setYoutubeUrl('');
      setYoutubeCaption('');
      loadExistingMedia();
    } catch (error) {
      console.error('Error adding YouTube video:', error);
      setMessage(error.response?.data?.error || 'Failed to add YouTube video');
    } finally {
      setLoading(false);
    }
  };

  const imageCount = existingMedia?.gallery?.filter(m => m.media_type === 'image').length || 0;
  const videoCount = existingMedia?.gallery?.filter(m => m.media_type === 'youtube').length || 0;

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px'
    },
    header: {
      marginBottom: '30px'
    },
    title: {
      fontSize: '28px',
      marginBottom: '10px'
    },
    content: {
      backgroundColor: 'white',
      padding: '30px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    section: {
      marginBottom: '40px',
      paddingBottom: '30px',
      borderBottom: '1px solid #e0e0e0'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '15px'
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      marginBottom: '10px'
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      marginRight: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    imageGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '15px',
      marginTop: '20px'
    },
    imageCard: {
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    imageThumb: {
      width: '100%',
      height: '150px',
      objectFit: 'cover'
    },
    imageInfo: {
      padding: '10px',
      backgroundColor: '#f8f9fa'
    },
    message: {
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '15px'
    },
    successMessage: {
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb'
    },
    errorMessage: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f5c6cb'
    },
    uploadBox: {
      border: '2px dashed #ddd',
      borderRadius: '8px',
      padding: '30px',
      textAlign: 'center',
      marginTop: '15px'
    },
    fileInput: {
      display: 'none'
    },
    mediaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      marginBottom: '15px'
    },
    mediaThumbnail: {
      width: '80px',
      height: '80px',
      objectFit: 'cover',
      borderRadius: '4px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={styles.title}>Media Library</h1>
            <p>Manage your business photos and videos</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {message && (
        <div style={{
          ...styles.message,
          ...(message.includes('Success') || message.includes('successfully') ? styles.successMessage : styles.errorMessage)
        }}>
          {message}
        </div>
      )}

      {(isUploading || uploadProgress > 0) && (
        <div style={{
          margin: '20px 0',
          padding: '10px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {uploadProgress === 100 ? 'Complete!' : 'Processing...'}
            </span>
            <span style={{ fontSize: '14px', color: '#007acc', fontWeight: 'bold' }}>
              {uploadProgress}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${uploadProgress}%`,
              height: '100%',
              backgroundColor: uploadProgress === 100 ? '#4CAF50' : '#007acc',
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      <div style={styles.content}>
        {/* Logo Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Logo</h2>
          {existingMedia?.logo ? (
            <div style={styles.mediaItem}>
              <img src={`https://api.bridalconnect.co.za${existingMedia.logo}`} alt="Logo" style={styles.mediaThumbnail} />
              <div style={{ flex: 1 }}>
                <strong>Current Logo</strong>
                <div style={{ fontSize: '12px', color: '#666' }}>Click replace to update</div>
              </div>
              <button
                onClick={() => handleReplaceImage('logo')}
                style={styles.button}
              >
                Replace Logo
              </button>
            </div>
          ) : (
            <div style={styles.uploadBox}>
              <p>Upload your business logo (square format recommended)</p>
              <input
                type="file"
                id="logo-upload"
                style={styles.fileInput}
                accept="image/*"
                onChange={(e) => handleFileUpload('logo', e.target.files)}
              />
              <label htmlFor="logo-upload">
                <span style={styles.button}>Upload Logo</span>
              </label>
            </div>
          )}
        </div>

        {/* Banner Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Banner</h2>
          {existingMedia?.banner ? (
            <div style={styles.mediaItem}>
              {existingMedia.banner.endsWith('.mp4') || existingMedia.banner.endsWith('.webm') ? (
                <video
                  src={`https://api.bridalconnect.co.za${existingMedia.banner}`}
                  style={styles.mediaThumbnail}
                  muted
                  loop
                  autoPlay
                />
              ) : (
                <img src={`https://api.bridalconnect.co.za${existingMedia.banner}`} alt="Banner" style={styles.mediaThumbnail} />
              )}
              <div style={{ flex: 1 }}>
                <strong>Current Banner</strong>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {existingMedia.banner.endsWith('.mp4') || existingMedia.banner.endsWith('.webm') ? 'Video banner' : 'Image banner'}
                </div>
              </div>
              <button
                onClick={() => handleReplaceImage('banner')}
                style={styles.button}
              >
                Replace Banner
              </button>
            </div>
          ) : (
            <div style={styles.uploadBox}>
              <p>Upload a banner image for your profile (16:4 ratio recommended)</p>
              <input
                type="file"
                id="banner-upload"
                style={styles.fileInput}
                accept="image/*"
                onChange={(e) => handleFileUpload('banner', e.target.files)}
              />
              <label htmlFor="banner-upload">
                <span style={styles.button}>Upload Banner</span>
              </label>
            </div>
          )}
        </div>

        {/* Gallery Section */}
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Gallery Images</h2>
            <span style={{ fontSize: '14px', color: imageCount >= 16 ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
              {imageCount}/16 images
            </span>
          </div>

          <div style={{
            backgroundColor: '#e7f3ff',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '15px',
            fontSize: '14px',
            border: '1px solid #b3d9ff'
          }}>
            <strong>💡 Tip:</strong> Upload high-quality images of your work. Images are automatically compressed to ensure fast loading. Maximum 16 images.
          </div>

          {imageCount < 16 && (
            <div style={styles.uploadBox}>
              <p>Upload images to your gallery (max 10 at once)</p>
              <input
                type="file"
                id="gallery-upload"
                style={styles.fileInput}
                accept="image/*"
                multiple
                onChange={(e) => handleFileUpload('gallery', e.target.files)}
              />
              <label htmlFor="gallery-upload">
                <span style={styles.button}>Upload Images</span>
              </label>
            </div>
          )}

          {imageCount > 0 && (
            <div style={styles.imageGrid}>
              {existingMedia.gallery.filter(item => item.media_type === 'image').map((item) => (
                <div key={item.id} style={{ ...styles.imageCard, position: 'relative' }}>
                  <img
                    src={`https://api.bridalconnect.co.za${item.url}`}
                    alt={item.caption || 'Gallery image'}
                    style={styles.imageThumb}
                  />
                  <button
                    onClick={() => handleDeleteMedia(item.id)}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                    title="Delete image"
                  >
                    ×
                  </button>
                  {item.caption && (
                    <div style={styles.imageInfo}>
                      <div style={{ fontSize: '12px' }}>{item.caption}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* YouTube Videos Section */}
        <div style={{ ...styles.section, borderBottom: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0 }}>YouTube Videos</h2>
            <span style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>
              {videoCount} video{videoCount !== 1 ? 's' : ''}
            </span>
          </div>

          <div style={{
            backgroundColor: '#fff3cd',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '15px',
            fontSize: '14px',
            border: '1px solid #ffc107'
          }}>
            <strong>🎥 YouTube Only:</strong> Paste YouTube video URLs to showcase your work. No direct video uploads.
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              YouTube URL
            </label>
            <input
              style={styles.input}
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              disabled={loading}
            />

            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Caption (Optional)
            </label>
            <input
              style={{ ...styles.input, marginBottom: '15px' }}
              type="text"
              placeholder="Add a caption for this video"
              value={youtubeCaption}
              onChange={(e) => setYoutubeCaption(e.target.value)}
              disabled={loading}
            />

            <button
              style={{ ...styles.button, ...(loading || !youtubeUrl ? styles.buttonDisabled : {}) }}
              onClick={handleAddYouTubeVideo}
              disabled={loading || !youtubeUrl}
            >
              {loading ? 'Adding...' : 'Add Video'}
            </button>
          </div>

          {videoCount > 0 && (
            <div style={styles.imageGrid}>
              {existingMedia.gallery.filter(item => item.media_type === 'youtube').map((item) => (
                <div key={item.id} style={{ ...styles.imageCard, position: 'relative' }}>
                  <iframe
                    width="100%"
                    height="150"
                    src={`https://www.youtube.com/embed/${new URL(item.url).searchParams.get('v')}`}
                    title={item.caption || 'YouTube video'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ borderRadius: '8px 8px 0 0' }}
                  />
                  <button
                    onClick={() => handleDeleteMedia(item.id)}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                    title="Delete video"
                  >
                    ×
                  </button>
                  {item.caption && (
                    <div style={styles.imageInfo}>
                      <div style={{ fontSize: '12px' }}>
                        🎥 {item.caption}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaManager;
